import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ApiErrorCode } from '../../common/enums';
import { ApiErrorResponse } from '../../common/models/ApiErrorResponse';
import { serviceNationMemberValidation } from '../../api/loginAPI';
import { Logger } from '../../config/logging/logger';
import { AUTO_LOGIN_ROUTE, HOME_ROUTE } from '../../routes/namedRoutes';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';
import { NavigationTarget, useNavigationUtils } from '../../common/utils/useNavigationUtils';

export default function SNForwardComponent() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openExternalLink } = useNavigationUtils();
  const snToken = searchParams.get('token');
  const edgeDestination = searchParams.get('destination') || HOME_ROUTE;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const logger = useMemo(() => Logger.create('SNForwardComponent'), []);

  useEffect(() => {
    const validateMember = async () => {
      try {
        const { tempAccessToken, member } = await serviceNationMemberValidation(snToken || '');

        if (!member || !member.email) {
          logger.error('SNForwardComponent: Missing member or member email after validation');
          setErrorMessage(t('error.generalMessage'));
          return;
        }
        const emailParam = encodeURIComponent(member.email);
        const afterLoginPathParam = encodeURIComponent(edgeDestination);
        const accessTokenParam = encodeURIComponent(tempAccessToken);
        navigate(`${AUTO_LOGIN_ROUTE}?email=${emailParam}&afterLoginPath=${afterLoginPathParam}&accessToken=${accessTokenParam}&loginSource=serviceNation`);
      } catch (err) {
        logger.error(Logger.format(err));
        const apiError = axios.isAxiosError(err) ?
          err.response?.data as ApiErrorResponse | undefined :
          undefined;

        // The email-in-another-solution-org message is written for the member and names the
        // conflicting solution org, so it is shown as-is rather than mapped to local copy.
        if (apiError?.code === ApiErrorCode.SsoMemberExistInOtherSolution) {
          setErrorMessage(apiError.message || t('error.snEmailInUse'));
          return;
        }

        setErrorMessage(t('error.generalMessage'));
      }
    };

    validateMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (<Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    marginLeft: 'auto',
    width: 'fit-content',
    marginRight: 'auto',
  }}>
    {errorMessage === null ? <><CircularProgress color="inherit" size={40} /></> : <>
      <Typography color={red[500]} variant="h6" align="center">
        {errorMessage || t('error.generalMessage')}
      </Typography>
      <OptimusButton
        customVariant='secondaryGray'
        buttonText={t('button.backToServiceNation') as string}
        data-testid={TestIds.signup.snBackBtn}
        onClick={() => openExternalLink(process.env.REACT_APP_SERVICE_NATION_URL || '/home', NavigationTarget.Webview)}
      /></>}
  </Box>);
}
